import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Self,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { AppControlType } from '@common/components/app-control/app-control.component';
import { ValidationMessageService } from '@common/services/validation-message.service';
import { marker } from '@common/translate-marker';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'lodash-es';
import { Observable, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, debounce, distinctUntilChanged, first } from 'rxjs/operators';
import { CommonService } from '../../../singleton-services/common.service';
import { AbstractFormControlComponent } from './abstract-form-control.component';

const noop = (item) => {
    if (item.localizedCode) return `${item.localizedCode} - ${item.name}`;
    if (item.code) return `${item.code} - ${item.name}`;
    if (item.id) return `${item.name}`;
};

/** Codelists too large to download at all - they are searched on the server when the dropdown
 *  is actually used (open/typing) and only the top matches are fetched. Preselected values are
 *  resolved with a small filterIds request instead of downloading the whole list.
 *  Names must match both the codeListName template usages and the BE LargeCodelists set. */
const LAZY_LOADED_CODELISTS = ['Port', 'Taric', 'NHM'];

const SERVER_FILTER_DEBOUNCE_MS = 1000;
const LOCAL_FILTER_DEBOUNCE_MS = 250;

@Component({
    selector: 'form-dropdown',
    styleUrls: ['form-controls.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <div
            class="form-group"
            [ngClass]="{
                'has-value': !!value || hasValue,
                'no-label': !labelText,
                'mb-0': !margin
            }">
            @if (labelText) {
                <label class="col-form-label" [ngClass]="{ 'form-control-disabled': isDisabled }">
                    {{ labelText }}
                </label>
            }
            @if (contextual) {
                <i
                    class="fa fa-info-circle h6 position-absolute right-0 z-index-1"
                    kendoTooltip
                    title="{{ contextual | translate }}"
                    aria-hidden="true"></i>
            }
            @if (!multi) {
                <kendo-combobox
                    class="form-control form-control-sm"
                    placement="top"
                    tooltipClass="error-tooltip"
                    [data]="data"
                    [textField]="displayValue"
                    [valueField]="valueField"
                    [disabled]="isDisabled"
                    [loading]="isBusy"
                    [valuePrimitive]="true"
                    [filterable]="true"
                    [title]="getTooltip(value)"
                    [ngClass]="{ 'is-invalid': isInvalid }"
                    [placeholder]="placeholder"
                    [(ngModel)]="value"
                    (open)="onDropdownOpen()"
                    (filterChange)="handleFilter($event)"
                    (valueChange)="changeValue($event)"
                    (focus)="focusInHandler()"
                    (blur)="focusOutHandler()"></kendo-combobox>
            }
            @if (multi) {
                <kendo-multiselect
                    class="multiselect"
                    placement="top"
                    tooltipClass="error-tooltip"
                    [data]="data"
                    [textField]="displayValue"
                    [valueField]="valueField"
                    [disabled]="isDisabled"
                    [loading]="isBusy"
                    [valuePrimitive]="true"
                    [filterable]="true"
                    [title]="getTooltip(value)"
                    [placeholder]="placeholder"
                    [ngClass]="{ 'is-invalid': isInvalid, 'is-read-only': isDisabled, required: required }"
                    [(ngModel)]="value"
                    (open)="onDropdownOpen()"
                    (filterChange)="handleFilter($event)"
                    (valueChange)="changeValue($event)"
                    (focus)="focusInHandler()"
                    (blur)="focusOutHandler()"></kendo-multiselect>
            }
            @if (multi && !isBusy && value?.length > 0) {
                <kendo-badge class="multiselect-count" themeColor="dark">
                    {{ value.length }}
                </kendo-badge>
            }
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DropDownComponent
    extends AbstractFormControlComponent
    implements OnInit, OnDestroy, OnChanges, AfterContentInit
{
    @Input() labelText: string;
    @Input() codeListName: string;
    @Input() codeListValues: any = [];
    @Input() displayValue = 'label';
    @Input() valueField = 'id';
    @Input() contextual: string = null;
    @Input() forTOSSystem?: string = '';
    @Input() selectLabel = noop;
    @Input() hasValue = false;
    @Input() margin = true;
    @Input() multi = false;
    @Input() observableInput: Observable<any>;
    @Input() type: AppControlType;
    @Input() placeholder: string;
    @Input() automaticPopulateWithFirstValue: boolean = false; // only when we have 1 item in dropdown list
    @Input() required = false;
    @Input() isDisabled: boolean;
    @Input() cacheKey: string;
    /** When true, the codelist is not downloaded on init but on first use (open/typing).
     *  Enabled automatically for codelists in LAZY_LOADED_CODELISTS. */
    @Input() lazyLoad = false;
    @Output() valueChange = new EventEmitter<any>();

    observableInputExistingValue: any = null;
    automaticPopulated: boolean = false; // to track if automatic population has already been done

    isBusy = true;

    untouchedData: any = [];
    data: any = [];
    dataSubscription: any;

    private loadState: 'pending' | 'loading' | 'loaded' = 'loading';
    private lazyValueSub: Subscription;
    private serverSearchSub: Subscription;
    /** True between subscribing to observableInput and its first emission, so filtering does not
     *  clear a spinner that belongs to a request still on its way. */
    private observableInputInFlight = false;

    private filterValues: Subject<string> = new Subject<string>();
    searchSub: Subscription;
    valueChangesSub: Subscription;
    statusChangesSub: Subscription;
    private _tooltip;

    @ViewChild('tooltip', { static: false }) set tooltip(value) {
        this._tooltip = value;
        if (value) {
            this.showFormControlValidation();
        }
    }

    get tooltip() {
        return this._tooltip;
    }

    constructor(
        @Self() @Optional() controlDir: NgControl,
        private commonService: CommonService,
        private translateService: TranslateService,
        validationMessageService: ValidationMessageService
    ) {
        super(controlDir, validationMessageService);

        // server-searched codelists are debounced long enough to avoid a request per keystroke,
        // locally filtered ones only need enough to coalesce fast typing
        this.searchSub = this.filterValues
            .pipe(debounce(() => timer(this.isLazy ? SERVER_FILTER_DEBOUNCE_MS : LOCAL_FILTER_DEBOUNCE_MS)))
            .subscribe((x) => {
                this.filterItems(x);
            });
    }

    public clearSelectedItems(): void {
        this.value = [];
        this.handleFilter(this.value);
    }

    /** Handle value changes when form value is set programmatically - if id is not in initially spliced list, filter is triggered.
     *  eg. after container id input, we create a query to get containerISO CODE for that container and fill that data automatically */
    ngAfterContentInit(): void {
        this.valueChangesSub = this.controlDir?.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
            if (value) {
                // server-filtered codelist - resolve just the selected item(s) instead of filtering,
                // the local data only ever holds the current page of search results
                if (this.isLazy) {
                    this.resolveLazyValueItems(value);
                    return;
                }

                // codelist values are still loading; extractInitialData will validate/select the value once data arrives
                if (this.codeListName && this.loadState !== 'loaded') {
                    return;
                }

                const itemInSplicedList = this.data.find((dataItem) => dataItem?.id == value);
                if (itemInSplicedList) {
                    return;
                }

                if (!itemInSplicedList) {
                    this.handleFilter(value);
                    return;
                }
            }

            if (!value) {
                this.data = this.untouchedData.slice(0, 250);
            }
        });

        // updating tooltip on every validation change
        this.statusChangesSub = this.controlDir?.statusChanges.subscribe(() => {
            this.showFormControlValidation();
        });
    }

    maybeApplyActive() {
        this.hasValue =
            !!this.value ||
            (this.value === 0 && this.type === AppControlType.Number) ||
            (this.value === false && this.type === AppControlType.YesNo);
    }

    focusInHandler() {
        this.hasValue = !!this.value || !this.isDisabled;
        this.placeholder = this.translateService.instant(marker('Select')) + ' ' + this.labelText;
    }

    focusOutHandler() {
        this.hasValue = false;
        this.maybeApplyActive();
        this.placeholder = null;
    }

    changeValue(e) {
        this.markTouched();
        this.onChange(e);

        this.showFormControlValidation();

        this.valueChange.emit(e);
    }

    writeValue(value: any) {
        super.writeValue(value);
    }

    getTooltip(value) {
        if (!value) return this.translateService.instant(marker(this.multi ? 'Select value(s)' : 'Select value'));
        else return '';
    }

    get isLazy(): boolean {
        return !!this.codeListName && (this.lazyLoad || LAZY_LOADED_CODELISTS.includes(this.codeListName));
    }

    //filtering
    ngOnInit() {
        this.isBusy = true;

        if (this.observableInput) {
            this.handleObservableInput();
        }
        if (this.codeListName) {
            if (this.isLazy) {
                this.loadState = 'pending';
                this.isBusy = false;
                if (this.value) this.resolveLazyValueItems(this.value);
            } else {
                this.handleCodeListNameInput();
            }
        } else if (this.codeListValues) {
            this.handleCodelistValuesInput();
        } else {
            this.isBusy = false;
            throw new Error('Either codeListName or codeListValue expected to be passed into the dropdown component.');
        }

        if (this.getFormControlValidators) {
            this.showFormControlValidation();
        }
    }

    /**
     *  Given a codelist Name (eg. CodelistDocumentType) use getDropdownOptions() to make an http call to return the data
     *  getDropdownOptions caches the retrieved data on the client.
     */
    private handleCodeListNameInput() {
        this.loadState = 'loading';
        const dataSubject = this.commonService.getDropdownOptions(this.codeListName, this.forTOSSystem);

        this.dataSubscription = dataSubject.subscribe((data) => {
            if (data == null) return;
            data = data.map((x: any) => ({
                label: this.selectLabel(x),
                id: x.id,
                name: x.name,
                localizedCode: x.localizedCode
            }));

            this.untouchedData = data;

            this.extractInitialData();
            this.loadState = 'loaded';
            this.isBusy = false;
        });
    }

    onDropdownOpen() {
        this.loadDataIfPending();
    }

    /** Server-filtered codelists are fetched on first use instead of on init. */
    private loadDataIfPending() {
        if (this.loadState !== 'pending') return;
        this.serverSearch('');
    }

    /** Fetch the top matches for the term from the server - the whole codelist is never
     *  downloaded. Items selected in the control are kept in the local data so their
     *  labels stay visible even when they are not part of the current search results. */
    private serverSearch(term: string) {
        if (this.loadState === 'pending') {
            this.loadState = 'loading';
        }
        this.isBusy = true;
        this.serverSearchSub?.unsubscribe();
        this.serverSearchSub = this.commonService
            .searchCodeListItems(this.codeListName, term)
            .pipe(first())
            .subscribe({
                next: (items) => {
                    const mapped = (items || []).map((x: any) => ({
                        label: this.selectLabel(x),
                        id: x.id,
                        name: x.name,
                        localizedCode: x.localizedCode
                    }));

                    const selectedIds = (Array.isArray(this.value) ? this.value : [this.value])
                        .filter((val) => val != null && val !== '')
                        .map((val) => (val?.id != null ? val.id : val));
                    const selectedItems = this.untouchedData.filter(
                        (item) => selectedIds.some((id) => id == item.id) && !mapped.some((m) => m.id === item.id)
                    );

                    this.untouchedData = [...mapped, ...selectedItems];
                    this.data = this.untouchedData;
                    this.loadState = 'loaded';
                    this.isBusy = false;
                },
                error: () => {
                    this.isBusy = false;
                    // let the next open/typing retry the fetch
                    this.loadState = 'pending';
                }
            });
    }

    /** Resolve only the selected item(s) of a server-filtered codelist so their labels can be
     *  displayed without downloading the whole list. */
    private resolveLazyValueItems(value: any) {
        const ids = (Array.isArray(value) ? value : [value])
            .map((val) => (val?.id != null ? val.id : val))
            .filter((id) => id != null && id !== '');

        const missingIds = ids.filter((id) => !this.untouchedData.some((x) => x.id == id));
        if (missingIds.length === 0) return;

        this.isBusy = true;
        this.lazyValueSub?.unsubscribe();
        this.lazyValueSub = this.commonService
            .getCodeListItemsByIds(this.codeListName, missingIds)
            .pipe(first())
            .subscribe({
                next: (items) => {
                    this.isBusy = false;

                    (items || []).forEach((x: any) => {
                        if (this.untouchedData.some((d) => d.id === x.id)) return;
                        this.untouchedData.push({
                            label: this.selectLabel(x),
                            id: x.id,
                            name: x.name,
                            localizedCode: x.localizedCode
                        });
                    });
                    this.data = [...this.untouchedData];

                    // same behavior as the eager path: clear values that don't exist in the codelist
                    if (missingIds.some((id) => !this.untouchedData.some((d) => d.id == id))) {
                        this.controlDir?.reset();
                    }
                },
                error: () => {
                    this.isBusy = false;
                }
            });
    }

    /**
     *  Given an observable input (eg. this.someService.someBackendQuery()) subscribe directly to the given observable and retrieve the data.
     *  We're using this mainly for custom queries, eg. when we need to filter the dropdown results.
     *  Since it's primary use is for custom queries, eg. filtered list of WorkOrders we are not caching the results.
     *
     *  If custom query is initially null, let's say we have to select some form values before making a query (someQ = this.someService.someBackendQuery()),
     *  ngOnChanges will call this function again when someQ receives its value
     */
    private handleObservableInput() {
        if (this.value) this.observableInputExistingValue = this.value;

        // Use cached observable if cacheKey is provided
        const source$ = this.cacheKey
            ? this.commonService.cacheObservableInput(this.cacheKey, this.observableInput)
            : this.observableInput;

        // a re-assigned observableInput starts a new request - show the spinner for it too,
        // not just for the one started in ngOnInit
        this.isBusy = true;
        this.observableInputInFlight = true;
        this.dataSubscription?.unsubscribe();
        this.dataSubscription = source$
            .pipe(
                first(),
                catchError((err) => {
                    // without this the spinner would keep spinning for a request that never returns data
                    this.observableInputInFlight = false;
                    this.isBusy = false;
                    return throwError(() => err);
                })
            )
            .subscribe((data) => {
                this.observableInputInFlight = false;

                if (!data) {
                    this.isBusy = false;
                    return;
                }

                // When data comes from cache (shareReplay), it emits synchronously before the view is ready.
                // Wrapping in Promise.resolve() defers the update to the next microtask,
                // so Angular detects the change and the dropdown renders correctly.

                Promise.resolve().then(() => {
                    data = data.map((x: any) => ({
                        label: this.selectLabel(x),
                        id: x.id,
                        name: x.name,
                        localizedCode: x.localizedCode
                    }));

                    this.untouchedData = data;

                    // automatic population when only 1 item exists in dropdown list
                    if (this.automaticPopulateWithFirstValue && data.length === 1) {
                        this.observableInputExistingValue = this.value = data[0];
                        this.automaticPopulated = true;
                    }

                    // reset if more than 1 item exists and automatic population is enabled
                    else if (
                        this.automaticPopulateWithFirstValue &&
                        this.observableInputExistingValue &&
                        data.length > 1 &&
                        this.automaticPopulated
                    ) {
                        this.observableInputExistingValue = this.value = null;
                        this.automaticPopulated = false;
                    }

                    this.extractInitialData();
                    this.isBusy = false;
                });
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.observableInput && !changes.observableInput.firstChange && this.observableInput) {
            this.handleObservableInput();
        }
        if (changes.codeListValues) {
            this.handleCodelistValuesInput();
        }
    }

    private showFormControlValidation() {
        if (this.getFormControlValidators && this.tooltip) {
            this.tooltip.ngbTooltip = map(this.getFormControlValidators, (x) =>
                this.translateService.instant(marker(x))
            ).join('\n');
        } else if (this.tooltip) {
            this.tooltip.ngbTooltip = null;
        }
    }

    /**
     *  In some cases we can directly pass an object[] as values to this component. In this case, object[] needs to have values before the component is rendered,
     *  so, use *ngIf to prevent rendering the component before the data is loaded.
     */
    private handleCodelistValuesInput() {
        // this is when codelistValues has 1 element with value null. This happens when we get null for that property from DisplayModel on BE
        if (this.codeListValues[0] === null) {
            this.isBusy = false;
            return;
        }

        // No values to seed the dropdown with - the data is still on its way through observableInput.
        // Running extractInitialData() against the empty list here would clear a value that was set on the
        // form control before the component was created (pre-filled/locked forms), so leave it to the
        // observableInput subscription, which calls extractInitialData() once the data actually arrives.
        if (this.observableInput && !this.codeListValues.length) {
            return;
        }

        this.codeListValues = this.codeListValues.map((x: any) => ({
            label: this.selectLabel(x),
            id: x.id,
            name: x.name,
            code: x.code,
            localizedCode: x.localizedCode
        }));

        this.untouchedData = this.codeListValues;

        this.extractInitialData();

        if (!this.observableInput) {
            this.isBusy = false;
        }
    }

    handleFilter(value) {
        this.isBusy = true;
        this.filterValues.next(value);
    }

    private extractInitialData() {
        // Remove duplicates from untouchedData
        const uniqueIds = new Set();
        this.untouchedData = this.untouchedData.filter((item) => {
            if (uniqueIds.has(item.id)) {
                return false;
            } else {
                uniqueIds.add(item.id);
                return true;
            }
        });

        this.data = this.untouchedData.slice(0, 250);

        if (this.value && !this.observableInput) {
            const values = Array.isArray(this.value) ? this.value : [this.value];
            values.forEach((val) => {
                const id = val.id || val;
                const itemFound = this.data.find((x) => x.id == id) || this.untouchedData.find((x) => x.id == id);

                if (!itemFound) {
                    this.controlDir.reset();
                    return;
                }

                // Add item only if it's not already in the data array
                if (!this.data.some((x) => x.id === itemFound.id)) {
                    this.data.push(itemFound);
                }
            });
        }

        if (!this.value && this.observableInput && this.observableInputExistingValue) {
            this.value = this.observableInputExistingValue;

            const id = this.value.id ? this.value.id : this.value;
            if (this.data.find((x) => x.id == id)) {
                this.writeValue(id);
                return;
            }

            const itemFound = this.untouchedData.find((x) => x.id == id);

            if (!itemFound) {
                this.controlDir.reset();
                return;
            }

            // Add item only if it's not already in the data array
            if (!this.data.some((x) => x.id === itemFound.id)) {
                this.data.push(itemFound);
            }
        }

        if (this.value && this.observableInput && this.observableInputExistingValue) {
            const id = this.value.id ? this.value.id : this.value;
            if (this.data.find((x) => x.id == id)) {
                this.writeValue(id);
                return;
            }

            const itemFound = this.untouchedData.find((x) => x.id == id);
            if (!itemFound) {
                this.controlDir.reset();
                return;
            }

            // Add item only if it's not already in the data array
            if (!this.data.some((x) => x.id === itemFound.id)) {
                this.data.push(itemFound);
            }
        }
    }

    filterItems(value: any) {
        // server-filtered codelist - ask the server for the matching page instead of filtering locally
        if (this.isLazy) {
            const term = typeof value === 'string' ? value : '';
            // nothing loaded and nothing searched for - stay unloaded until the dropdown is really used
            if (this.loadState === 'pending' && term === '') {
                this.isBusy = false;
                return;
            }
            this.serverSearch(term);
            return;
        }

        if (typeof value === 'string') {
            this.data = this.untouchedData
                .filter((s) => s[this.displayValue].toLowerCase().includes(value.toLowerCase()))
                .slice(0, 250);
        }

        // an empty list because the observableInput data is still on its way is not a failed match -
        // extractInitialData() validates the value once the data actually arrives
        if (this.data.length === 0 && this.untouchedData.length > 0) {
            this.controlDir.reset();
        }

        // filtering is local and synchronous, so it is always finished here - only an observableInput
        // request that is still on its way may keep the spinner up
        this.isBusy = this.observableInputInFlight;
    }

    ngOnDestroy() {
        this.dataSubscription?.unsubscribe();
        this.searchSub?.unsubscribe();
        this.valueChangesSub?.unsubscribe();
        this.statusChangesSub?.unsubscribe();
        this.lazyValueSub?.unsubscribe();
        this.serverSearchSub?.unsubscribe();
    }

    public isItemSelected(val: any) {
        return this.value?.some((x) => x === val);
    }
}
